import type { TOC } from '@ember/component/template-only';
import { guidFor } from '@ember/object/internals';
import { service } from '@ember/service';
import { capitalize } from '@ember/string';
import Component from '@glimmer/component';
import type { IntlService } from 'ember-intl';
import { Popover } from 'ember-primitives';
import ValueWithUnit from 'parog-web/components/value-with-unit';
import type BioExamResult from 'parog-web/models/bio-exam-result';
import type BioExamResultsReceivedJE from 'parog-web/models/bio-exam-results-received-journal-event';
import type JournalEventModel from 'parog-web/models/journal-event';
import {
  alertingStyleClassName,
  alertingStyleClassNameBlack
} from 'parog-web/modifiers/alerting-je-style';

export interface S {
  Args: {
    originJournalEvent?: JournalEventModel;
    cellResult: CellBioExamResult;

    /**
     * The id of the show references tooltip hook element,
     * provided by the parent component tracking the mouseover events
     * */
    activeReferencesHook?: string;
  };
}

export const referencesHookClass = 'cell-references-hook';

/**
 * Wrapping class around a possible BioExamResult to encapsulate
 * the alerting style associated with the BioExamResultsReceivedJE
 * */
export class CellBioExamResult {
  /** Styles to represent if there is an alert on the BioExamResultsReceivedJE for the LOINC code of this BioExamResult */
  get styles() {
    const isAlerting = this.result
      ? this.originJournalEvent.hasAlertForCode(this.result.code)
      : false;

    return isAlerting
      ? `${alertingStyleClassName} ${alertingStyleClassNameBlack}`
      : '';
  }

  constructor(
    public result: BioExamResult | undefined,
    public originJournalEvent: BioExamResultsReceivedJE
  ) {}
}

const ReferencesHook: TOC<{ Element: HTMLElement }> = <template>
  <i
    ...attributes
    class='{{referencesHookClass}}
      circle info icon hover:text-grey ml-[calc(0.5em_+_4px)] inline text-[#80808066]'
  ></i>
</template>;

export default class BioExamResultsSummaryRowCellComponent extends Component<S> {
  @service declare intl: IntlService;

  referencesHookId = guidFor(this);
  result: CellBioExamResult['result'];

  get displayedValue() {
    if (!this.result) return;
    const displayedValue = this.result.displayedValue;

    if (['positive', 'negative'].includes(displayedValue))
      return this.intl.t('bioExamResultCodes.value', { value: displayedValue });

    return typeof displayedValue === 'string'
      ? capitalize(displayedValue)
      : displayedValue;
  }

  get shouldDisplayReferences() {
    return this.#minValue || this.#maxValue;
  }

  /** Should we display the references tooltip for this cell */
  get showReferences() {
    return (
      this.args.activeReferencesHook &&
      this.args.activeReferencesHook === this.referencesHookId
    );
  }

  get tooltipText() {
    return `Références : [${this.#minValue ?? ' -'} : ${
      this.#maxValue ?? '- '
    }]`;
  }

  get unitStyles() {
    return this.args.cellResult.styles || 'text-[#808080]';
  }

  get #maxValue() {
    if (!this.result) return;
    return this.result.normValSupp;
  }

  get #minValue() {
    if (!this.result) return;
    return this.result.normValInf;
  }

  constructor(owner, args: S['Args']) {
    super(owner, args);
    this.result = args.cellResult.result;
  }

  <template>
    <td class='center aligned'>
      {{#if this.displayedValue}}
        {{#if this.result.unit}}
          <ValueWithUnit
            @value={{this.displayedValue}}
            @valueStyles={{@cellResult.styles}}
            @unit={{this.result.unit}}
            @unitStyles={{this.unitStyles}}
          />
        {{else}}
          {{#if @cellResult.styles}}
            <span class={{@cellResult.styles}}>{{this.displayedValue}}</span>
          {{else}}
            {{this.displayedValue}}
          {{/if}}
        {{/if}}

        {{#if this.shouldDisplayReferences}}
          {{#if this.showReferences}}
            <Popover
              @inline={{true}}
              @placement='top'
              @offsetOptions={{6}}
              as |p|
            >
              <ReferencesHook {{p.reference}} id={{this.referencesHookId}} />
              <p.Content
                class='border-1 border-grey-medium absolute left-0 top-0 w-max rounded bg-white p-4 shadow'
              >
                {{this.tooltipText}}
                <div
                  class='border-b-grey-medium border-r-grey-medium absolute h-2 w-2 rotate-45 border border-l-0 border-t-0 bg-white'
                  {{p.arrow}}
                ></div>
              </p.Content>
            </Popover>
          {{else}}
            <ReferencesHook id={{this.referencesHookId}} />
          {{/if}}
        {{/if}}
      {{else}}
        —
      {{/if}}
    </td>
  </template>
}
